import {
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  Typography,
  Modal,
  Box,
  Collapse,
} from "@mui/material";
import { ComoInput } from "../Common/ComoInput";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import {
  useMicrosoftStatus,
  useSharepointLinks,
  useLinkSharepointFolder,
  useSyncSharepointLink,
  useStopSharepointLink,
  useUnlinkSharepointFolder,
} from "../../Query/useSharepoint";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SyncIcon from "@mui/icons-material/Sync";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTranslation } from "react-i18next";
import { ProcessingStatus } from "../../Enums/companyStatus";
import color from "../../Constants/colors";
import { SharepointLink } from "../../Models/sharepointLink";
import React from "react";

interface SharepointSyncProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
}

const SharepointSync: React.FC<SharepointSyncProps> = ({
  open,
  onClose,
  companyId,
}) => {
  const { t } = useTranslation("sharepointSync");
  const navigate = useNavigate();

  const { data: microsoftStatus } = useMicrosoftStatus(open);
  const { data: linksData, isLoading } = useSharepointLinks(companyId, open);

  const linkFolderMutation = useLinkSharepointFolder(companyId);
  const syncLinkMutation = useSyncSharepointLink(companyId);
  const stopLinkMutation = useStopSharepointLink(companyId);
  const unlinkFolderMutation = useUnlinkSharepointFolder(companyId);

  const [folderUrl, setFolderUrl] = useState("");
  const [expandedErrorsLinkId, setExpandedErrorsLinkId] = useState<
    number | null
  >(null);
  const [unlinkTargetId, setUnlinkTargetId] = useState<number | null>(null);

  const canLink = !!microsoftStatus?.connected && !microsoftStatus?.revoked;

  const queryClient = useQueryClient();
  const anySyncing =
    linksData?.links.some(
      (link) => link.status === ProcessingStatus.IN_PROGRESS,
    ) ?? false;
  const wasSyncingRef = useRef(false);

  useEffect(() => {
    if (wasSyncingRef.current && !anySyncing) {
      queryClient.invalidateQueries({ queryKey: ["documents", companyId] });
    }
    wasSyncingRef.current = anySyncing;
  }, [anySyncing, companyId, queryClient]);

  const handleLinkFolder = () => {
    if (!folderUrl) return;
    linkFolderMutation.mutate(folderUrl, {
      onSuccess: () => {
        setFolderUrl("");
      },
    });
  };

  const handleToggleErrors = (linkId: number) => {
    setExpandedErrorsLinkId((prev) => (prev === linkId ? null : linkId));
  };

  const handleUnlink = (linkId: number) => {
    setUnlinkTargetId(linkId);
  };

  const handleConfirmUnlink = (confirmed: boolean) => {
    if (confirmed && unlinkTargetId !== null) {
      unlinkFolderMutation.mutate(unlinkTargetId);
    }
    setUnlinkTargetId(null);
  };

  const handleGoToProfile = () => {
    navigate("/profile");
  };

  const getStatusIcon = (status: number) => {
    switch (status) {
      case ProcessingStatus.IN_PROGRESS:
        return (
          <CircularProgress size={16} sx={{ color: color.accent, mr: 1 }} />
        );
      case ProcessingStatus.PROCESSED:
        return (
          <CheckCircleOutlineIcon
            sx={{ color: color.green, fontSize: 20, mr: 1 }}
          />
        );
      case ProcessingStatus.ERROR:
      case ProcessingStatus.TIME_LIMIT_EXCEEDED:
        return (
          <ErrorOutlineIcon sx={{ color: color.red, fontSize: 20, mr: 1 }} />
        );
      default:
        return (
          <FolderOutlinedIcon
            sx={{ color: color.primary_dark, fontSize: 20, mr: 1 }}
          />
        );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        padding: "20px",
      }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "8px",
          border: `1px solid ${color.gray200}`,
          width: "100%",
          maxWidth: "900px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
            borderBottom: `1px solid ${color.gray200}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <CloudSyncIcon sx={{ color: color.accent }} />
            <Typography
              variant="h6"
              sx={{
                margin: 0,
                fontSize: "1.25rem",
                fontWeight: 600,
                color: color.primary_dark,
              }}
            >
              {t("sharepointSync.title")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              padding: "8px",
              borderRadius: "4px",
              "&:hover": { backgroundColor: color.gray100 },
            }}
          >
            <CloseIcon sx={{ color: color.gray500 }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            flex: 1,
            overflow: "hidden",
            minHeight: "450px",
          }}
        >
          <Box
            sx={{
              flex: "1 1 50%",
              padding: "32px",
              borderRight: `1px solid ${color.gray200}`,
              display: "flex",
              flexDirection: "column",
              backgroundColor: color.gray50,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                mb: 3,
                fontWeight: 600,
                color: color.primary_dark,
              }}
            >
              {t("sharepointSync.linkNewFolder")}
            </Typography>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {microsoftStatus && !canLink && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      p: 1.5,
                      backgroundColor: color.orange50,
                      border: `1px solid ${color.orange200}`,
                      borderRadius: "8px",
                    }}
                  >
                    <WarningAmberIcon
                      sx={{ color: color.orange600, fontSize: 20 }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: color.orange600, display: "block" }}
                      >
                        {microsoftStatus.revoked
                          ? t("sharepointSync.reconnectMicrosoft")
                          : t("sharepointSync.connectMicrosoftFirst")}
                      </Typography>
                      <Button
                        size="small"
                        onClick={handleGoToProfile}
                        sx={{
                          mt: 0.5,
                          p: 0,
                          minWidth: 0,
                          color: color.accent,
                          textTransform: "none",
                          fontWeight: 600,
                          "&:hover": { backgroundColor: "transparent" },
                        }}
                      >
                        {t("sharepointSync.goToProfile")}
                      </Button>
                    </Box>
                  </Box>
                )}
                <ComoInput
                  value={folderUrl}
                  onChange={(e) => setFolderUrl(e.target.value)}
                  placeholder={t("sharepointSync.folderUrlPlaceholder")}
                  primaryColor={color.primary_dark}
                  multiline={false}
                  maxRows={1}
                  size="medium"
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleLinkFolder}
                  disabled={
                    !folderUrl || !canLink || linkFolderMutation.isPending
                  }
                  sx={{
                    py: 1.5,
                    backgroundColor: color.accent,
                    color: color.white,
                    "&:hover": {
                      backgroundColor: color.accent_dark,
                      color: color.white,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: color.btn_dark_disabled,
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                    fontWeight: 600,
                    boxShadow: "none",
                  }}
                >
                  {linkFolderMutation.isPending ? (
                    <CircularProgress size={24} sx={{ color: color.white }} />
                  ) : (
                    t("sharepointSync.linkFolder")
                  )}
                </Button>
              </Box>
            </Box>

            <Box
              sx={{
                mt: "auto",
                pt: 2,
                borderTop: `1px solid ${color.gray200}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: color.gray500,
                  display: "block",
                }}
              >
                {t("sharepointSync.syncNote")}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              flex: "1 1 50%",
              padding: "32px",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                mb: 3,
                fontWeight: 600,
                color: color.primary_dark,
              }}
            >
              {t("sharepointSync.linkedFolders")}
            </Typography>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
                pr: 1,
              }}
            >
              {isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress sx={{ color: color.accent }} />
                </Box>
              ) : linksData?.links && linksData.links.length > 0 ? (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {linksData.links.map((link: SharepointLink) => (
                    <Box
                      key={link.id}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "8px",
                        border: `1px solid ${color.gray200}`,
                        padding: "16px",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: color.accent,
                          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                          backgroundColor: `${color.accent}05`,
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {getStatusIcon(link.status)}

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Tooltip title={link.folder_url}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: color.primary_dark,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {link.folder_path || link.folder_url}
                            </Typography>
                          </Tooltip>
                          <Typography
                            variant="caption"
                            sx={{
                              color: color.gray500,
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {link.last_synced_at
                              ? t("sharepointSync.lastSynced", {
                                  date: new Date(
                                    link.last_synced_at,
                                  ).toLocaleString(),
                                })
                              : t("sharepointSync.neverSynced")}
                            {" · "}
                            {t("sharepointSync.linkedBy", {
                              name: link.linked_by,
                            })}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            ml: 2,
                          }}
                        >
                          {link.status === ProcessingStatus.IN_PROGRESS ? (
                            <Tooltip title={t("sharepointSync.stopSync")}>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  stopLinkMutation.mutate(link.id)
                                }
                                sx={{
                                  color: color.red,
                                  "&:hover": { backgroundColor: color.red50 },
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title={t("sharepointSync.syncNow")}>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  syncLinkMutation.mutate(link.id)
                                }
                                disabled={
                                  link.account_revoked ||
                                  syncLinkMutation.isPending
                                }
                                sx={{
                                  color: color.accent,
                                  "&:hover": { backgroundColor: color.blue50 },
                                }}
                              >
                                <SyncIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          <Tooltip title={t("sharepointSync.unlink")}>
                            <IconButton
                              size="small"
                              onClick={() => handleUnlink(link.id)}
                              disabled={
                                link.status === ProcessingStatus.IN_PROGRESS
                              }
                              sx={{
                                color: color.gray400,
                                "&:hover": {
                                  color: color.red,
                                  backgroundColor: color.red50,
                                },
                              }}
                            >
                              <LinkOffIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {link.account_revoked && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 1,
                          }}
                        >
                          <WarningAmberIcon
                            sx={{ color: color.orange600, fontSize: 16 }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ color: color.orange600 }}
                          >
                            {t("sharepointSync.accountRevokedRow")}
                          </Typography>
                        </Box>
                      )}

                      {link.last_sync_report && (
                        <Box sx={{ mt: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: color.gray500 }}
                            >
                              {t("sharepointSync.reportSummary", {
                                added: link.last_sync_report.added,
                                updated: link.last_sync_report.updated,
                                deleted: link.last_sync_report.deleted,
                                skipped:
                                  link.last_sync_report.skipped_unsupported,
                              })}
                            </Typography>
                            {link.last_sync_report.errors.length > 0 && (
                              <Button
                                size="small"
                                onClick={() => handleToggleErrors(link.id)}
                                endIcon={
                                  expandedErrorsLinkId === link.id ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )
                                }
                                sx={{
                                  p: 0,
                                  minWidth: 0,
                                  color: color.red600,
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                  },
                                }}
                              >
                                {t("sharepointSync.showErrors", {
                                  count: link.last_sync_report.errors.length,
                                })}
                              </Button>
                            )}
                          </Box>
                          {link.last_sync_report.errors.length > 0 && (
                            <Collapse in={expandedErrorsLinkId === link.id}>
                              <Box
                                sx={{
                                  mt: 1,
                                  p: 1.5,
                                  backgroundColor: color.red50,
                                  border: `1px solid ${color.red200}`,
                                  borderRadius: "8px",
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.5,
                                }}
                              >
                                {link.last_sync_report.errors.map(
                                  (reportError, index) => (
                                    <Typography
                                      key={index}
                                      variant="caption"
                                      sx={{
                                        color: color.red700,
                                        display: "block",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {reportError.name} — {reportError.error}
                                    </Typography>
                                  ),
                                )}
                              </Box>
                            </Collapse>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: color.gray50,
                    borderRadius: "8px",
                    border: `1px dashed ${color.gray300}`,
                    textAlign: "center",
                    p: 4,
                  }}
                >
                  <Typography variant="body2" sx={{ color: color.gray500 }}>
                    {t("sharepointSync.noLinkedFolders")}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <ConfirmationDialog
          open={unlinkTargetId !== null}
          onClose={handleConfirmUnlink}
          title={t("sharepointSync.unlinkConfirmTitle")}
          description={t("sharepointSync.unlinkConfirmMessage")}
          content={t("sharepointSync.unlinkConfirmMessage")}
          actions={[
            {
              label: t("sharepointSync.no"),
              onClick: () => handleConfirmUnlink(false),
            },
            {
              label: t("sharepointSync.yes"),
              onClick: () => handleConfirmUnlink(true),
            },
          ]}
        />
      </Box>
    </Modal>
  );
};

export default SharepointSync;
